(() => {
  'use strict';
  const configNode = document.getElementById('talsem-native-config');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  const contextKey = JSON.stringify([config.store, config.country, config.currency, config.taxesIncluded, config.catalog]);
  try { sessionStorage.setItem('talsem:native-context', contextKey); } catch { /* Storage is optional. */ }
  const pricing = window.TalsemNativePricing;
  const carts = window.TalsemShopifyCart;
  const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const t = (key, values = {}) => String(config.labels[key] || '').replace(/\{(\w+)\}/g, (match, name) => values[name] ?? match);
  const money = cents => new Intl.NumberFormat(config.locale, { style: 'currency', currency: config.currency, currencyDisplay: 'code' }).format(cents / 100);
  const root = document.getElementById('b2b-app');
  const taxNote = () => t(config.taxesIncluded ? 'tax_included' : 'tax_excluded');
  const icon = name => {
    const key = name.replace(/(^|-)(\w)/g, (_, separator, letter) => letter.toUpperCase());
    const shape = window.lucide?.icons?.[key];
    return shape ? window.lucide.createElement(shape, { 'aria-hidden': 'true' }).outerHTML : '';
  };
  const href = (path, hash = '') => {
    const url = new URL(path, location.origin);
    if (url.origin !== location.origin) return '#';
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    url.hash = hash;
    return url.href;
  };
  const review = () => `<a href="${escape(href(config.cart))}">${escape(t('review_basket'))}</a>`;
  const reload = () => `<button type="button" class="btn btn--secondary" data-native-reload>${escape(t('reload'))}</button>`;
  function errorText(error) {
    if (/CART_(WRITE_UNCERTAIN|RELOAD_REQUIRED|QUANTITIES_CHANGED|LINE_CHANGED|CONTEXT_CHANGED)/.test(error?.message)) return t('cart_changed');
    if (/MOQ|QUANTITY|REQUIRES_REVIEW|VARIANT_UNAVAILABLE/.test(error?.message)) return t('basket_error');
    return t('price_error');
  }
  let eligibility, client, cartClient, references, mutationBusy = false, basketLoading = false;
  let cartSnapshot, cartSnapshotAt = 0, cartRead, refreshPending;
  const cartSignature = cart => JSON.stringify([cart.currency, cart.taxes_included, cart.items, cart.total_price, cart.total_discount, cart.attributes]);
  async function readCart(force = false) {
    if (cartRead) return cartRead;
    if (!force && cartSnapshot && Date.now() - cartSnapshotAt < 2000) return cartSnapshot;
    const pending = cartClient.get();
    cartRead = pending;
    try {
      const cart = await pending;
      if (cartRead !== pending && cartSnapshot) return cartSnapshot;
      if (cartRead === pending) { cartSnapshot = cart; cartSnapshotAt = Date.now(); }
      return cart;
    } catch (error) {
      if (cartRead !== pending && cartSnapshot) return cartSnapshot;
      throw error;
    } finally { if (cartRead === pending) cartRead = null; }
  }
  function counts(cart) {
    const count = cart.items.reduce((sum, item) => sum + item.quantity, 0);
    document.querySelectorAll('[data-bulk-count]').forEach(node => { node.textContent = count; });
  }
  function notify(cart) {
    cartRead = null; cartSnapshot = cart; cartSnapshotAt = Date.now();
    counts(cart);
    window.dispatchEvent(new CustomEvent('talsem:native-basket-changed', { detail: { cart } }));
    try { localStorage.setItem('talsem:native-basket-revision', String(Date.now())); } catch { /* Native cart still persists without local storage. */ }
  }
  function blockControls(scope, busy) {
    scope.setAttribute('aria-busy', String(busy));
    if (busy) scope.querySelectorAll('button,input').forEach(node => { node.disabled = true; });
  }
  async function mutate(action) {
    if (mutationBusy) throw new Error('SHOPIFY_CART_BUSY');
    mutationBusy = true;
    try { const cart = await action(); notify(cart); return cart; }
    finally { mutationBusy = false; }
  }
  document.addEventListener('submit', event => {
    if (event.target.matches('.product-single__form')) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('click', event => {
    if (event.target.closest('[data-native-reload]')) location.reload();
  });

  function prepareSummary(wrapper) {
    const summary = wrapper.querySelector('[data-native-summary]');
    if (!summary || wrapper.dataset.nativeSummaryReady) return;
    wrapper.dataset.nativeSummaryReady = 'true';
    wrapper.closest('[data-section-type="product"]')?.addEventListener('variantChange', event => {
      const id = String(event.detail.variant?.id || '');
      if (id === wrapper.dataset.initialVariant) return;
      const template = [...wrapper.querySelectorAll('[data-native-summary-variant]')].find(node => node.dataset.nativeSummaryVariant === id);
      wrapper.dataset.initialVariant = id;
      summary.replaceChildren(template ? template.content.cloneNode(true) : document.createTextNode(t('unavailable')));
      summary.hidden = false;
      const interactive = wrapper.querySelector('[data-native-interactive]');
      if (interactive) interactive.hidden = true;
    });
  }

  function productControl(wrapper) {
    prepareSummary(wrapper);
    const el = wrapper.querySelector('[data-native-interactive]') || wrapper;
    const summary = wrapper.querySelector('[data-native-summary]');
    const container = wrapper.closest('[data-section-type="product"]');
    let variants;
    try { variants = JSON.parse(container.querySelector('[data-variant-json]').textContent); }
    catch { el.textContent = t('unavailable'); return; }
    let selected = variants.find(variant => String(variant.id) === wrapper.dataset.initialVariant);
    let quantity, generation = 0, estimateGeneration = 0, verified = false, busy = false, timer, stickyObserver;
    let estimatedKey, estimatedAt = 0, estimatingKey, renderedReferences;
    const status = text => { const node = el.querySelector('[data-native-status]'); if (node) node.textContent = text; };
    function updating(pending) {
      el.dataset.nativePricingPending = String(pending);
      el.querySelector('[data-native-total]')?.setAttribute('aria-busy', String(pending));
      const note = el.querySelector('[data-native-price-pending]');
      if (note) note.textContent = pending ? t('updating_price', { quantity }) : '';
    }
    function showTotal(line, quote) {
      const values = { unit: line ? money(line.unitPriceCents) : '--',
        combined: line ? t('combined_line', { quantity: line.quantity, total: money(line.totalCents) }) : '--',
        basket: quote ? t('basket_after', { total: money(quote.totalCents) }) : '--' };
      for (const [name, value] of Object.entries(values)) {
        const node = el.querySelector(`[data-native-total-${name}]`);
        if (node && node.textContent !== value) node.textContent = value;
      }
      el.querySelectorAll('[data-trade-sticky-price]').forEach(node => {
        const value = line ? `${quantity} x ${money(line.unitPriceCents)}` : '--';
        if (node.textContent !== value) node.textContent = value;
      });
    }
    function buttons(enabled) {
      el.querySelectorAll('[data-native-add],[data-native-direct]').forEach(node => { node.disabled = !enabled || busy; });
    }
    async function estimate(cart) {
      const total = el.querySelector('[data-native-total]');
      const input = el.querySelector('[data-native-quantity]');
      if (!input || !total) return;
      if (!input.checkValidity()) {
        ++estimateGeneration; estimatedKey = estimatingKey = null; verified = false; buttons(false);
        updating(false); showTotal(); status(t('whole_quantity', { quantity: input.min })); return;
      }
      const id = String(selected.id), adding = quantity, view = generation;
      let run;
      try {
        cart ||= await readCart();
        if (view !== generation || adding !== quantity || !el.isConnected) return;
        const key = JSON.stringify([id, adding, cartSignature(cart)]);
        if (key === estimatingKey || (key === estimatedKey && Date.now() - estimatedAt < 60000)) return;
        run = ++estimateGeneration; estimatingKey = key;
        verified = false; buttons(false); updating(true); status('');
        const basket = carts.lines(cart);
        const existing = basket.find(line => line.id === id);
        if (existing) existing.quantity += adding; else basket.push({ id, quantity: adding });
        const quote = await client.quote(basket);
        if (run !== estimateGeneration || !el.isConnected) return;
        const line = quote.lines.find(item => item.id === id);
        showTotal(line, quote);
        estimatedKey = key; estimatedAt = Date.now(); verified = true; buttons(true);
      } catch (error) {
        if (view !== generation || adding !== quantity || (run && run !== estimateGeneration)) return;
        verified = false; estimatedKey = null; buttons(false);
        updating(false); showTotal(); status(errorText(error));
      } finally {
        if (run === estimateGeneration) { estimatingKey = null; updating(false); }
      }
    }
    function loadReferences() {
      const run = generation;
      const body = el.querySelector('[data-native-references]');
      if (!body) return;
      const render = rows => {
        if (run !== generation || !el.isConnected) return;
        const signature = JSON.stringify(rows.map(row => [row.quantity, row.unitPriceCents, row.currency]));
        if (renderedReferences === signature) return;
        const html = rows.map(item => `<tr><td><button type="button" data-native-tier="${item.quantity}" ${busy ? 'disabled' : ''}>${item.quantity}+</button></td><td>${escape(money(item.unitPriceCents))}</td></tr>`).join('');
        body.innerHTML = html; renderedReferences = signature;
        if (summary) summary.hidden = true;
        el.querySelector('[data-native-reference-panel]').hidden = false;
      };
      references(String(selected.id), {
        priceCents: Number(selected.price),
        onProgress: rows => { if (!body.querySelector('[data-native-tier]')) render(rows); },
      }).then(rows => {
        render(rows);
        if (run === generation) el.querySelector('[data-native-tier-status]').textContent = '';
      }).catch(() => {
        if (run !== generation) return;
        renderedReferences = null;
        body.innerHTML = '<tr><td colspan="2">--</td></tr>';
        if (summary) { summary.hidden = false; el.querySelector('[data-native-reference-panel]').hidden = true; }
        el.querySelector('[data-native-tier-status]').textContent = t('tier_error');
      });
    }
    function draw() {
      ++generation;
      ++estimateGeneration; clearTimeout(timer); verified = false;
      estimatedKey = estimatingKey = renderedReferences = null;
      stickyObserver?.disconnect();
      el.hidden = false;
      const row = eligibility.variants[String(selected?.id)];
      const specs = container.querySelector('[data-trade-specs]');
      if (specs) {
        specs.querySelector('[data-trade-sku]').textContent = selected?.sku || '--';
        specs.querySelector('[data-trade-option]').textContent = selected?.title === 'Default Title' ? 'Standard' : selected?.title || '--';
        specs.querySelector('[data-trade-moq]').textContent = row ? t('minimum', { quantity: row.minimumQuantity }) : t('unavailable');
      }
      if (!row) { el.innerHTML = `<p>${escape(t('unavailable'))}</p>`; return; }
      quantity = row.minimumQuantity;
      el.innerHTML = `<div data-native-reference-panel ${summary ? 'hidden' : ''}>
        <h2>${escape(t('bulk_pricing'))}</h2><p>${escape(taxNote())}</p>
        <table><caption class="visually-hidden">${escape(t('reference_caption'))}</caption>
          <thead><tr><th>${escape(t('quantity'))}</th><th>${escape(t('unit_price'))}</th></tr></thead>
          <tbody data-native-references><tr><td colspan="2"><span data-nosnippet>${escape(t('loading'))}</span></td></tr></tbody>
        </table><p>${escape(t('reference_note'))}</p>
      </div><p><span data-native-tier-status data-nosnippet></span></p>
      <div class="talsem-bulk-line"><label>${escape(t('quantity_label'))}<input data-native-quantity type="number" min="${row.minimumQuantity}" max="100000" step="1" value="${quantity}" inputmode="numeric"></label><div class="talsem-bulk-total" data-native-total aria-live="polite" data-nosnippet><small>${escape(t('selection_price'))}</small><strong data-native-total-unit>--</strong><small>${escape(t('average_unit'))}</small><small data-native-total-combined>--</small><small data-native-total-basket>--</small><small class="native-price-pending" data-native-price-pending></small></div></div>
      <p>${escape(t('minimum', { quantity: row.minimumQuantity }))}</p>
      <button type="button" class="btn" data-native-add disabled>${escape(t('add'))}</button>
      <button type="button" class="btn btn--secondary talsem-bulk-direct" data-native-direct disabled>${escape(t('direct'))}</button>
      <p>${escape(t('availability'))}</p><p><span role="status" aria-live="polite" data-native-status data-nosnippet></span></p><p>${review()}</p>`;
      el.insertAdjacentHTML('beforeend', `<div class="trade-sticky-quote" hidden><span>${escape(t('selection_price'))}<strong data-trade-sticky-price>--</strong></span><button type="button" class="btn" data-native-direct disabled>${escape(t('direct'))}</button></div>`);
      const sticky = el.querySelector('.trade-sticky-quote');
      stickyObserver = new IntersectionObserver(entries => {
        sticky.hidden = entries[0].boundingClientRect.bottom >= 150;
      }, { rootMargin: '-150px 0px 0px 0px' });
      stickyObserver.observe(el.querySelector('.talsem-bulk-direct'));
      estimate();
      loadReferences();
    }
    container.addEventListener('variantChange', event => {
      if (String(selected?.id) === String(event.detail.variant?.id)) return;
      selected = event.detail.variant; draw();
    });
    el.addEventListener('input', event => {
      if (!event.target.matches('[data-native-quantity]')) return;
      const next = Number(event.target.value);
      if (next === quantity) return;
      quantity = next; estimatedKey = estimatingKey = null; verified = false; ++estimateGeneration; buttons(false);
      clearTimeout(timer);
      if (!event.target.checkValidity()) { estimate(); return; }
      updating(true); timer = setTimeout(estimate, 350);
    });
    el.addEventListener('click', async event => {
      const button = event.target.closest('button');
      if (!button || button.disabled || busy) return;
      if (button.dataset.nativeTier) {
        if (quantity === Number(button.dataset.nativeTier)) return;
        quantity = Number(button.dataset.nativeTier);
        estimatedKey = estimatingKey = null; verified = false; ++estimateGeneration; buttons(false);
        el.querySelector('[data-native-quantity]').value = quantity;
        clearTimeout(timer); updating(true); estimate(); return;
      }
      if (!(button.hasAttribute('data-native-add') || button.hasAttribute('data-native-direct')) || !verified) return;
      const id = String(selected.id), adding = quantity, direct = button.hasAttribute('data-native-direct');
      busy = true; blockControls(el, true); status(t('checking'));
      try {
        await mutate(() => cartClient.add(id, adding));
        busy = false; blockControls(el, false);
        el.querySelectorAll('[data-native-quantity],[data-native-tier]').forEach(node => { node.disabled = false; });
        if (direct) { location.assign(href(config.cart, 'b2b-quote-details')); return; }
        await estimate(); status(t('added'));
      } catch (error) {
        busy = false; blockControls(el, false); verified = false; buttons(false);
        status(errorText(error));
        el.querySelector('[data-native-status]').insertAdjacentHTML('beforeend', ` ${reload()}`);
      }
    });
    window.addEventListener('talsem:native-basket-changed', event => {
      if (el.isConnected && !busy) { estimate(event.detail?.cart); loadReferences(); }
    });
    draw();
  }

  function image(item) {
    const src = item.featured_image?.url || item.image;
    if (!src) return '<div class="talsem-bulk-image-placeholder"></div>';
    const url = new URL(src, location.origin);
    if (url.protocol !== 'https:' || !['cdn.shopify.com', 'talsem.com', 'www.talsem.com', config.store].includes(url.hostname)) return '<div class="talsem-bulk-image-placeholder"></div>';
    url.searchParams.set('width', '240');
    return `<img src="${escape(url.href)}" alt="${escape(item.product_title)}" width="120" height="120" loading="lazy">`;
  }
  const summaryRow = (label, value) => `<div class="b2b-summary-row"><span>${escape(label)}</span><span>${escape(value)}</span></div>`;
  let basketStructureKey;
  function blockBasket(pending) {
    root.setAttribute('aria-busy', String(pending));
    root.querySelectorAll('[data-native-line-quantity]').forEach(input => { input.readOnly = pending; });
    root.querySelectorAll('[data-native-step],[data-native-remove]').forEach(button => { button.setAttribute('aria-disabled', String(pending || button.disabled)); });
    if (pending) root.querySelector('[data-native-checkout]')?.setAttribute('disabled', '');
    const note = root.querySelector('[data-native-basket-pending]');
    if (note) note.textContent = pending ? t('updating_basket') : '';
  }
  function patchBasket(next) {
    const previousLines = [...root.querySelectorAll('[data-native-line]')];
    const nextLines = [...next.querySelectorAll('[data-native-line]')];
    if (!previousLines.length || previousLines.length !== nextLines.length) return false;
    for (let i = 0; i < previousLines.length; i++) {
      const previous = previousLines[i], current = nextLines[i];
      previous.dataset.nativeLine = current.dataset.nativeLine;
      const input = previous.querySelector('[data-native-line-quantity]');
      const value = current.querySelector('[data-native-line-quantity]');
      if (input && value) {
        if (input.value !== value.value) input.value = value.value;
        input.defaultValue = value.defaultValue;
        input.readOnly = false;
      }
      const price = previous.querySelector('.b2b-line-money');
      const newPrice = current.querySelector('.b2b-line-money');
      if (price.innerHTML !== newPrice.innerHTML) price.innerHTML = newPrice.innerHTML;
      const controls = [...previous.querySelectorAll('[data-native-step],[data-native-remove]')];
      [...current.querySelectorAll('[data-native-step],[data-native-remove]')].forEach((button, index) => {
        controls[index].disabled = button.disabled;
      });
    }
    for (const selector of ['.b2b-basket-head > span', '.b2b-summary', '[data-native-cart-error]', '[data-native-cart-recovery]']) {
      const previous = root.querySelector(selector), current = next.querySelector(selector);
      if (previous.innerHTML !== current.innerHTML) previous.innerHTML = current.innerHTML;
    }
    const button = root.querySelector('[data-native-checkout]'), current = next.querySelector('[data-native-checkout]');
    button.disabled = current.disabled;
    if (button.innerHTML !== current.innerHTML) button.innerHTML = current.innerHTML;
    return true;
  }
  function renderBasket(cart, quote, error = '') {
    const intro = `<section class="b2b-intro"><h1>${escape(t('basket'))}</h1><p>${escape(t('basket_intro'))}</p></section>`;
    root.setAttribute('aria-busy', 'false');
    if (!cart.items.length) { basketStructureKey = null; root.innerHTML = `<div class="b2b-wrap">${intro}<div class="b2b-empty"><p>${escape(t('empty'))}</p><a class="b2b-primary" href="${escape(href(config.routesRoot))}">${escape(t('browse'))}</a></div></div>`; return; }
    const structure = JSON.stringify(cart.items.map(item => [item.variant_id, item.product_title, item.variant_title, item.sku, item.url, item.image, item.featured_image, item.properties, item.gift_card, item.selling_plan_allocation]));
    const html = `<div class="b2b-wrap">${intro}<div class="b2b-basket-layout"><section aria-label="${escape(t('basket'))}"><div class="b2b-basket-head"><span>${escape(t('items', { variants: cart.items.length, quantity: cart.item_count }))}</span><a href="${escape(href(config.routesRoot))}">${escape(t('browse'))}</a></div>${cart.items.map(item => {
      const id = String(item.variant_id), row = eligibility.variants[id];
      const line = quote?.lines.find(value => value.id === id);
      const supported = row && !item.selling_plan_allocation && !item.gift_card && !Object.keys(item.properties || {}).length;
      return `<article class="b2b-line" data-native-line="${escape(item.key)}">${image(item)}<div><h3><a href="${escape(href(item.url))}">${escape(item.product_title)}</a></h3><p>${escape(item.variant_title || '')}${item.sku ? `<br>${escape(item.sku)}` : ''}</p>${supported ? `<p>${escape(t('minimum', { quantity: row.minimumQuantity }))}</p><div class="b2b-qty"><button type="button" data-native-step="-1" aria-label="${escape(t('decrease'))}" ${item.quantity <= row.minimumQuantity ? 'disabled' : ''}>${icon('minus') || '&#8722;'}</button><input type="number" data-native-line-quantity min="${row.minimumQuantity}" max="100000" step="1" inputmode="numeric" value="${item.quantity}" aria-label="${escape(t('quantity'))}"><button type="button" data-native-step="1" aria-label="${escape(t('increase'))}" ${item.quantity >= 100000 ? 'disabled' : ''}>${icon('plus') || '+'}</button></div>` : `<p class="b2b-error">${escape(t('unavailable_line'))}</p>`}</div><div class="b2b-line-money">${line?.discountCents ? `<del>${escape(money(line.baseTotalCents))}</del>` : ''}${line ? escape(money(line.totalCents)) : '--'}<span>${line ? `${escape(money(line.unitPriceCents))} ${escape(t('average_unit'))}` : ''}</span><button type="button" class="b2b-icon" data-native-remove aria-label="${escape(t('remove', { title: item.product_title }))}" title="${escape(t('remove', { title: item.product_title }))}">${icon('trash-2') || '&#215;'}</button></div></article>`;
    }).join('')}<p class="b2b-small">${escape(t('basket_note'))}</p></section><section class="b2b-form-panel" id="b2b-quote-details" tabindex="-1" aria-label="${escape(t('title'))}"><div class="b2b-summary">${quote ? summaryRow(t('before_savings'), money(quote.baseSubtotalCents)) + summaryRow(`${t('savings')}${quote.discountBps ? ` (${(quote.discountBps / 100).toFixed(2)}%)` : ''}`, `-${money(quote.discountCents)}`) : ''}${summaryRow(t('product_estimate'), quote ? money(quote.totalCents) : '--')}${summaryRow(t('shipping'), t('to_be_quoted'))}${summaryRow(t('taxes'), t('confirmed_quote'))}</div><p class="native-price-pending" data-native-basket-pending role="status"></p><p>${escape(taxNote())}</p><h2>${escape(t('title'))}</h2><p>${escape(t('details'))}</p><p>${escape(t('payment'))}</p>${config.enabled === true ? '' : `<p class="b2b-test-message">${escape(t('preview'))}</p>`}<p class="b2b-error" data-native-cart-error role="alert">${escape(error)}</p><button class="b2b-primary" type="button" data-native-checkout ${config.enabled === true && quote && !error ? '' : 'disabled'}>${escape(t('continue'))} ${icon('arrow-right')}</button><div data-native-cart-recovery>${error ? reload() : ''}</div></section></div></div>`;
    const next = document.createElement('template');
    next.innerHTML = html;
    if (structure !== basketStructureKey || !patchBasket(next.content)) root.replaceChildren(next.content);
    basketStructureKey = structure;
    blockBasket(false);
  }
  let renderedBasket;
  async function loadBasket(existing, { passive = false } = {}) {
    if (!root || basketLoading) return;
    if (passive && [...root.querySelectorAll('[data-native-line-quantity]')].some(input => input.value !== input.defaultValue)) return;
    if (passive && renderedBasket?.key === cartSignature(existing) && Date.now() - renderedBasket.at < 60000 && root.getAttribute('aria-busy') !== 'true') return;
    basketLoading = true; blockBasket(true);
    try {
      const cart = existing || await readCart(true); counts(cart);
      let quote, error = '';
      try {
        quote = await client.quote(carts.lines(cart));
        carts.verify(cart, quote, config.taxesIncluded);
      } catch (failure) { quote = null; error = errorText(failure); }
      renderBasket(cart, quote, error);
      renderedBasket = error ? null : { key: cartSignature(cart), at: Date.now() };
    } catch (error) {
      renderedBasket = null; basketStructureKey = null;
      root.setAttribute('aria-busy', 'false');
      root.innerHTML = `<div class="b2b-wrap"><p role="alert">${escape(errorText(error))}</p>${reload()}</div>`;
    } finally { basketLoading = false; }
  }
  async function changeLine(line, quantity) {
    if (mutationBusy) return;
    if (cartSnapshot?.items.find(item => item.key === line.dataset.nativeLine)?.quantity === quantity) {
      await loadBasket(cartSnapshot); return;
    }
    blockBasket(true);
    try { const cart = await mutate(() => cartClient.set(line.dataset.nativeLine, quantity)); await loadBasket(cart); }
    catch (error) {
      await loadBasket();
      const status = root.querySelector('[data-native-cart-error]');
      if (status) status.textContent = errorText(error);
      root.querySelector('[data-native-checkout]')?.setAttribute('disabled', '');
    }
  }
  root?.addEventListener('change', event => {
    const input = event.target;
    if (!input.matches('[data-native-line-quantity]') || mutationBusy) return;
    if (!input.checkValidity()) { input.reportValidity(); root.querySelector('[data-native-checkout]')?.setAttribute('disabled', ''); return; }
    changeLine(input.closest('[data-native-line]'), Number(input.value));
  });
  root?.addEventListener('input', event => {
    if (event.target.matches('[data-native-line-quantity]')) root.querySelector('[data-native-checkout]')?.setAttribute('disabled', '');
  });
  root?.addEventListener('click', async event => {
    const button = event.target.closest('button');
    if (!button || button.disabled || mutationBusy || basketLoading) return;
    const line = button.closest('[data-native-line]');
    if (button.hasAttribute('data-native-remove')) return changeLine(line, 0);
    if (button.dataset.nativeStep) {
      const input = line.querySelector('input');
      const quantity = Math.max(Number(input.min), Number(input.value) + Number(button.dataset.nativeStep));
      return changeLine(line, quantity);
    }
    if (!button.hasAttribute('data-native-checkout')) return;
    if (config.guestQuoteEndpoint) {
      if (window.TalsemGuestQuote && cartSnapshot) await window.TalsemGuestQuote.open(cartSnapshot, button);
      else root.querySelector('[data-native-cart-error]')?.replaceChildren(document.createTextNode(t('basket_error')));
      return;
    }
    mutationBusy = true; blockControls(root, true); button.textContent = t('checking');
    try {
      const result = await cartClient.prepare({ enabled: config.enabled });
      mutationBusy = false;
      location.assign(result.checkoutUrl);
    } catch (error) {
      mutationBusy = false; await loadBasket();
      const status = root.querySelector('[data-native-cart-error]');
      if (status) status.textContent = errorText(error);
      root.querySelector('[data-native-checkout]')?.setAttribute('disabled', '');
    }
  });

  function init(scope = document) {
    scope.querySelectorAll('[data-bulk-price]:not([data-native-ready])').forEach(el => {
      el.dataset.nativeReady = 'true';
      try {
        const rows = JSON.parse(el.querySelector('[data-bulk-grid-variants]').textContent)
          .filter(row => eligibility.variants[String(row.id)] && Number.isSafeInteger(row.price) && row.price > 0);
        if (!rows.length) { el.textContent = t('unavailable'); return; }
        if (el.querySelector('[data-native-grid-summary]')) return;
        el.innerHTML = `${escape(t('from', { price: money(Math.min(...rows.map(row => row.price))) }))} ${escape(t('unit'))}<small class="talsem-bulk-grid-note">${escape(t('grid_note'))}</small>`;
        const minimums = rows.map(row => eligibility.variants[String(row.id)].minimumQuantity);
        const range = Math.min(...minimums) === Math.max(...minimums) ? String(minimums[0]) : `${Math.min(...minimums)}-${Math.max(...minimums)}`;
        el.insertAdjacentHTML('beforeend', `<small class="talsem-bulk-grid-minimum">${escape(t('minimum', { quantity: range }))}</small>`);
      } catch { if (!el.querySelector('[data-native-grid-summary]')) el.textContent = t('price_error'); }
    });
    scope.querySelectorAll('[data-bulk-order]:not([data-native-ready])').forEach(el => {
      el.dataset.nativeReady = 'true'; productControl(el);
    });
  }
  async function start() {
    document.querySelectorAll('[data-bulk-order]').forEach(prepareSummary);
    try {
      const response = await fetch(config.catalog, { credentials: 'omit', signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error('MAPPING_UNAVAILABLE');
      eligibility = pricing.validateEligibility(await response.json());
      client = pricing.createClient({ ...config, eligibility, origin: location.origin });
      cartClient = carts.createClient({ ...config, eligibility, pricingClient: client, origin: location.origin });
      let storage;
      try { storage = sessionStorage; } catch { /* In-memory caching still works in private browsers. */ }
      references = pricing.createReferenceFinder(client, eligibility, { storage, revision: config.catalog });
      init();
      if (root) {
        await loadBasket();
        if (location.hash === '#b2b-quote-details') root.querySelector('#b2b-quote-details')?.focus();
      } else { try { counts(await readCart()); } catch { /* The product controls report cart errors. */ } }
      document.addEventListener('shopify:section:load', event => init(event.target));
      new MutationObserver(records => {
        for (const record of records) for (const node of record.addedNodes) {
          if (node.nodeType === 1 && node.querySelector('[data-bulk-price]:not([data-native-ready]),[data-bulk-order]:not([data-native-ready])')) init(node);
        }
      }).observe(document.body, { subtree: true, childList: true });
    } catch (error) {
      document.querySelectorAll('[data-bulk-price],[data-bulk-order]').forEach(node => {
        const status = node.querySelector('[data-native-start-error]');
        if (status) status.textContent = errorText(error);
        else if (!node.querySelector('[data-native-grid-summary]')) node.textContent = errorText(error);
      });
      if (root) { root.setAttribute('aria-busy', 'false'); root.innerHTML = `<p role="alert">${escape(errorText(error))}</p>${reload()}`; }
    }
  }
  async function refresh(force = false) {
    if (!cartClient || mutationBusy || refreshPending || (!force && Date.now() - cartSnapshotAt < 10000)) return;
    refreshPending = true;
    try {
      const cart = await readCart(true);
      counts(cart);
      if (root) await loadBasket(cart, { passive: true });
      window.dispatchEvent(new CustomEvent('talsem:native-basket-changed', { detail: { cart } }));
    } catch (error) {
      // Only a changed market requires new Liquid-rendered currency/context data.
      if (error.message === 'SHOPIFY_CART_CONTEXT_CHANGED') location.reload();
    } finally { refreshPending = false; }
  }
  window.addEventListener('storage', event => { if (event.key === 'talsem:native-basket-revision') refresh(true); });
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    try {
      const latest = sessionStorage.getItem('talsem:native-context');
      if (latest && latest !== contextKey) { location.reload(); return; }
    } catch { /* Cart currency is still checked without storage. */ }
    refresh(true);
  });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
